/*
 * Poly2Tri Copyright (c) 2009-2014, Poly2Tri Contributors
 * http://code.google.com/p/poly2tri/
 *
 * poly2tri.js (JavaScript port) (c) 2009-2014, Poly2Tri Contributors
 * https://github.com/r3mi/poly2tri.js
 *
 * All rights reserved.
 *
 * Distributed under the 3-clause BSD License, see LICENSE.txt
 */

import { type XY, toString as xyToString } from './xy'

export interface Edge {
	p: XY
	q: XY
}

/**
 * Triangle class.
 * Triangle-based data structures are known to have better performance than
 * quad-edge structures.
 * See: J. Shewchuk, "Triangle: Engineering a 2D Quality Mesh Generator and
 * Delaunay Triangulator", "Triangulations in CGAL"
 */
export class Triangle {
	private points_: [XY, XY, XY]
	private neighbors_: [Triangle | null, Triangle | null, Triangle | null]
	private interior_ = false
	constrained_edge: [boolean, boolean, boolean] = [false, false, false]
	delaunay_edge: [boolean, boolean, boolean] = [false, false, false]

	constructor(a: XY, b: XY, c: XY) {
		this.points_ = [a, b, c]
		this.neighbors_ = [null, null, null]
	}

	/**
	 * For pretty printing ex. <code>"[(5;42)(10;20)(21;30)]"</code>.
	 */
	toString(): string {
		return `[${xyToString(this.points_[0])}${xyToString(this.points_[1])}${xyToString(this.points_[2])}]`
	}

	/**
	 * Get one vertex of the triangle.
	 * The output triangles of a triangulation have vertices which are references
	 * to the initial input points (not copies): any custom fields in the
	 * initial points can be retrieved in the output triangles.
	 */
	getPoint(index: number): XY {
		return this.points_[index]
	}

	/**
	 * For backward compatibility
	 */
	GetPoint = this.getPoint

	/**
	 * Get all 3 vertices of the triangle as an array
	 */
	getPoints(): [XY, XY, XY] {
		return this.points_
	}

	getNeighbor(index: number): Triangle | null {
		return this.neighbors_[index]
	}

	/**
	 * Test if this Triangle contains the Point object given as parameter as one of its vertices.
	 * Only point references are compared, not values.
	 */
	containsPoint(point: XY): boolean {
		const points = this.points_
		// Here we are comparing point references, not values
		return point === points[0] || point === points[1] || point === points[2]
	}

	/**
	 * Test if this Triangle contains the Edge object given as parameter as its
	 * bounding edges. Only point references are compared, not values.
	 */
	containsEdge(edge: Edge): boolean {
		return this.containsPoint(edge.p) && this.containsPoint(edge.q)
	}

	/**
	 * Test if this Triangle contains the two Point objects given as parameters among its vertices.
	 * Only point references are compared, not values.
	 */
	containsPoints(p1: XY, p2: XY): boolean {
		return this.containsPoint(p1) && this.containsPoint(p2)
	}

	/**
	 * Has this triangle been marked as an interior triangle?
	 */
	isInterior(): boolean {
		return this.interior_
	}

	/**
	 * Mark this triangle as an interior triangle
	 */
	setInterior(interior: boolean): this {
		this.interior_ = interior
		return this
	}

	/**
	 * Update neighbor pointers.
	 */
	markNeighborPointers(p1: XY, p2: XY, t: Triangle): void {
		const points = this.points_
		// Here we are comparing point references, not values
		if ((p1 === points[2] && p2 === points[1]) || (p1 === points[1] && p2 === points[2])) {
			this.neighbors_[0] = t
		} else if ((p1 === points[0] && p2 === points[2]) || (p1 === points[2] && p2 === points[0])) {
			this.neighbors_[1] = t
		} else if ((p1 === points[0] && p2 === points[1]) || (p1 === points[1] && p2 === points[0])) {
			this.neighbors_[2] = t
		} else {
			throw new Error('poly2tri Invalid Triangle.markNeighborPointers() call')
		}
	}

	/**
	 * Exhaustive search to update neighbor pointers
	 */
	markNeighbor(t: Triangle): void {
		const points = this.points_
		if (t.containsPoints(points[1], points[2])) {
			this.neighbors_[0] = t
			t.markNeighborPointers(points[1], points[2], this)
		} else if (t.containsPoints(points[0], points[2])) {
			this.neighbors_[1] = t
			t.markNeighborPointers(points[0], points[2], this)
		} else if (t.containsPoints(points[0], points[1])) {
			this.neighbors_[2] = t
			t.markNeighborPointers(points[0], points[1], this)
		}
	}

	clearNeighbors(): void {
		this.neighbors_[0] = null
		this.neighbors_[1] = null
		this.neighbors_[2] = null
	}

	clearDelaunayEdges(): void {
		this.delaunay_edge[0] = false
		this.delaunay_edge[1] = false
		this.delaunay_edge[2] = false
	}

	/**
	 * Returns the point clockwise to the given point.
	 */
	pointCW(p: XY): XY | null {
		const points = this.points_
		// Here we are comparing point references, not values
		if (p === points[0]) {
			return points[2]
		} else if (p === points[1]) {
			return points[0]
		} else if (p === points[2]) {
			return points[1]
		} else {
			return null
		}
	}

	/**
	 * Returns the point counter-clockwise to the given point.
	 */
	pointCCW(p: XY): XY | null {
		const points = this.points_
		// Here we are comparing point references, not values
		if (p === points[0]) {
			return points[1]
		} else if (p === points[1]) {
			return points[2]
		} else if (p === points[2]) {
			return points[0]
		} else {
			return null
		}
	}

	/**
	 * Returns the neighbor clockwise to given point.
	 */
	neighborCW(p: XY): Triangle | null {
		// Here we are comparing point references, not values
		if (p === this.points_[0]) {
			return this.neighbors_[1]
		} else if (p === this.points_[1]) {
			return this.neighbors_[2]
		} else {
			return this.neighbors_[0]
		}
	}

	/**
	 * Returns the neighbor counter-clockwise to given point.
	 */
	neighborCCW(p: XY): Triangle | null {
		// Here we are comparing point references, not values
		if (p === this.points_[0]) {
			return this.neighbors_[2]
		} else if (p === this.points_[1]) {
			return this.neighbors_[0]
		} else {
			return this.neighbors_[1]
		}
	}

	getConstrainedEdgeCW(p: XY): boolean {
		// Here we are comparing point references, not values
		if (p === this.points_[0]) {
			return this.constrained_edge[1]
		} else if (p === this.points_[1]) {
			return this.constrained_edge[2]
		} else {
			return this.constrained_edge[0]
		}
	}

	getConstrainedEdgeCCW(p: XY): boolean {
		// Here we are comparing point references, not values
		if (p === this.points_[0]) {
			return this.constrained_edge[2]
		} else if (p === this.points_[1]) {
			return this.constrained_edge[0]
		} else {
			return this.constrained_edge[1]
		}
	}

	getConstrainedEdgeAcross(p: XY): boolean {
		// Here we are comparing point references, not values
		if (p === this.points_[0]) {
			return this.constrained_edge[0]
		} else if (p === this.points_[1]) {
			return this.constrained_edge[1]
		} else {
			return this.constrained_edge[2]
		}
	}

	setConstrainedEdgeCW(p: XY, ce: boolean): void {
		// Here we are comparing point references, not values
		if (p === this.points_[0]) {
			this.constrained_edge[1] = ce
		} else if (p === this.points_[1]) {
			this.constrained_edge[2] = ce
		} else {
			this.constrained_edge[0] = ce
		}
	}

	setConstrainedEdgeCCW(p: XY, ce: boolean): void {
		// Here we are comparing point references, not values
		if (p === this.points_[0]) {
			this.constrained_edge[2] = ce
		} else if (p === this.points_[1]) {
			this.constrained_edge[0] = ce
		} else {
			this.constrained_edge[1] = ce
		}
	}

	getDelaunayEdgeCW(p: XY): boolean {
		// Here we are comparing point references, not values
		if (p === this.points_[0]) {
			return this.delaunay_edge[1]
		} else if (p === this.points_[1]) {
			return this.delaunay_edge[2]
		} else {
			return this.delaunay_edge[0]
		}
	}

	getDelaunayEdgeCCW(p: XY): boolean {
		// Here we are comparing point references, not values
		if (p === this.points_[0]) {
			return this.delaunay_edge[2]
		} else if (p === this.points_[1]) {
			return this.delaunay_edge[0]
		} else {
			return this.delaunay_edge[1]
		}
	}

	setDelaunayEdgeCW(p: XY, e: boolean): void {
		// Here we are comparing point references, not values
		if (p === this.points_[0]) {
			this.delaunay_edge[1] = e
		} else if (p === this.points_[1]) {
			this.delaunay_edge[2] = e
		} else {
			this.delaunay_edge[0] = e
		}
	}

	setDelaunayEdgeCCW(p: XY, e: boolean): void {
		// Here we are comparing point references, not values
		if (p === this.points_[0]) {
			this.delaunay_edge[2] = e
		} else if (p === this.points_[1]) {
			this.delaunay_edge[0] = e
		} else {
			this.delaunay_edge[1] = e
		}
	}

	/**
	 * The neighbor across to given point.
	 */
	neighborAcross(p: XY): Triangle | null {
		// Here we are comparing point references, not values
		if (p === this.points_[0]) {
			return this.neighbors_[0]
		} else if (p === this.points_[1]) {
			return this.neighbors_[1]
		} else {
			return this.neighbors_[2]
		}
	}

	oppositePoint(t: Triangle, p: XY): XY | null {
		const cw = t.pointCW(p)
		return cw ? this.pointCW(cw) : null
	}

	/**
	 * Legalize triangle by rotating clockwise around oPoint
	 */
	legalize(opoint: XY, npoint: XY): void {
		const points = this.points_
		// Here we are comparing point references, not values
		if (opoint === points[0]) {
			points[1] = points[0]
			points[0] = points[2]
			points[2] = npoint
		} else if (opoint === points[1]) {
			points[2] = points[1]
			points[1] = points[0]
			points[0] = npoint
		} else if (opoint === points[2]) {
			points[0] = points[2]
			points[2] = points[1]
			points[1] = npoint
		} else {
			throw new Error('poly2tri Invalid Triangle.legalize() call')
		}
	}

	/**
	 * Returns the index of a point in the triangle.
	 * The point *must* be a reference to one of the triangle's vertices.
	 */
	index(p: XY): number {
		const points = this.points_
		// Here we are comparing point references, not values
		if (p === points[0]) {
			return 0
		} else if (p === points[1]) {
			return 1
		} else if (p === points[2]) {
			return 2
		} else {
			throw new Error('poly2tri Invalid Triangle.index() call')
		}
	}

	edgeIndex(p1: XY, p2: XY): number {
		const points = this.points_
		// Here we are comparing point references, not values
		if (p1 === points[0]) {
			if (p2 === points[1]) {
				return 2
			} else if (p2 === points[2]) {
				return 1
			}
		} else if (p1 === points[1]) {
			if (p2 === points[2]) {
				return 0
			} else if (p2 === points[0]) {
				return 2
			}
		} else if (p1 === points[2]) {
			if (p2 === points[0]) {
				return 1
			} else if (p2 === points[1]) {
				return 0
			}
		}
		return -1
	}

	/**
	 * Mark an edge of this triangle as constrained.
	 */
	markConstrainedEdgeByIndex(index: number): void {
		this.constrained_edge[index] = true
	}

	/**
	 * Mark an edge of this triangle as constrained.
	 */
	markConstrainedEdgeByEdge(edge: Edge): void {
		this.markConstrainedEdgeByPoints(edge.p, edge.q)
	}

	/**
	 * Mark an edge of this triangle as constrained.
	 * This method takes two Point instances defining the edge of the triangle.
	 */
	markConstrainedEdgeByPoints(p: XY, q: XY): void {
		const points = this.points_
		// Here we are comparing point references, not values
		if ((q === points[0] && p === points[1]) || (q === points[1] && p === points[0])) {
			this.constrained_edge[2] = true
		} else if ((q === points[0] && p === points[2]) || (q === points[2] && p === points[0])) {
			this.constrained_edge[1] = true
		} else if ((q === points[1] && p === points[2]) || (q === points[2] && p === points[1])) {
			this.constrained_edge[0] = true
		}
	}
}

